import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { fetchTenants, createCase } from '../api/client';
import TenantPicker from './TenantPicker';

/**
 * CaseCreate - Form to create a new investigation case.
 *
 * Creates a case with:
 *   - Tenant selection
 *   - UPN (user principal name) to investigate
 *   - First Known Indicator of Compromise (`iocDate`) - the earliest date suspicious
 *     activity is KNOWN to have occurred, i.e. the seed the case is opened from. It is
 *     NOT a finding about when compromise began, and must never be labelled or described
 *     as "the compromise date" - the investigation's job is to work backwards from it.
 *   - Optional end date
 *   - Optional ticket number and notes
 */
export default function CaseCreate() {
  const navigate = useNavigate();

  // Tenant loading state
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState(null);

  // Form fields
  const [tenantId, setTenantId] = useState('');
  const [upn, setUpn] = useState('');
  const [iocDate, setIocDate] = useState('');
  const [daysBack, setDaysBack] = useState('3');
  const [ticketNumber, setTicketNumber] = useState('');
  const [notes, setNotes] = useState('');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Load tenants on mount
  useEffect(() => {
    let cancelled = false;
    async function loadTenants() {
      setTenantsLoading(true);
      setTenantsError(null);
      try {
        const data = await fetchTenants();
        if (!cancelled) {
          setTenants(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setTenantsError(err.message || 'Failed to load tenants');
        }
      } finally {
        if (!cancelled) {
          setTenantsLoading(false);
        }
      }
    }
    loadTenants();
    return () => { cancelled = true; };
  }, []);

  // Form validation
  const isValid = tenantId && upn.trim() && iocDate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const selectedTenant = tenants.find((t) => t.TenantID === tenantId);

      // Compute date range from IOC date and days back
      const ioc = new Date(iocDate + 'T00:00:00Z');
      const start = new Date(ioc);
      start.setUTCDate(start.getUTCDate() - parseInt(daysBack, 10));
      const end = new Date(ioc);
      end.setUTCDate(end.getUTCDate() + 1);

      const payload = {
        TenantId: tenantId,
        TenantName: selectedTenant?.FriendlyName || '',
        UserPrincipalName: upn.trim(),
        StartDate: start.toISOString(),
        EndDate: end.toISOString(),
        IocDate: iocDate,
      };
      if (ticketNumber.trim()) payload.TicketNumber = ticketNumber.trim();
      if (notes.trim()) payload.Notes = notes.trim();

      const result = await createCase(payload);
      navigate(`/cases/${result.caseId || result.CaseID}`);
    } catch (err) {
      setSubmitError(err.message || 'Failed to create case');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/cases')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Dashboard
        </button>
        <h1 className="text-2xl font-bold text-gray-100">New Case</h1>
        <p className="text-sm text-gray-500 mt-1">
          Create a new M365 security investigation case
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card space-y-6">
          {/* Tenant Picker */}
          <TenantPicker
            tenants={tenants}
            value={tenantId}
            onChange={setTenantId}
            loading={tenantsLoading}
            error={tenantsError}
          />

          {/* UPN */}
          <div>
            <label
              htmlFor="upn"
              className="block text-sm font-medium text-gray-300 mb-1.5"
            >
              User Principal Name (UPN) <span className="text-red-400">*</span>
            </label>
            <input
              id="upn"
              type="email"
              value={upn}
              onChange={(e) => setUpn(e.target.value)}
              placeholder="user@contoso.com"
              className="input-field"
              required
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-gray-600">
              The email address of the user account to investigate
            </p>
          </div>

          {/* IOC Date & Days Back */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="ioc-date"
                className="block text-sm font-medium text-gray-300 mb-1.5"
              >
                First Known Indicator of Compromise <span className="text-red-400">*</span>
              </label>
              <input
                id="ioc-date"
                type="date"
                value={iocDate}
                onChange={(e) => setIocDate(e.target.value)}
                className="input-field"
                required
              />
              <p className="mt-1 text-xs text-gray-600">
                The earliest known date of suspicious activity
              </p>
            </div>

            <div>
              <label
                htmlFor="days-back"
                className="block text-sm font-medium text-gray-300 mb-1.5"
              >
                Days Back <span className="text-gray-600">(before IOC date)</span>
              </label>
              <select
                id="days-back"
                value={daysBack}
                onChange={(e) => setDaysBack(e.target.value)}
                className="input-field appearance-none cursor-pointer"
              >
                {Array.from({ length: 30 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d} {d === 1 ? 'day' : 'days'}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-600">
                How many days before the IOC date to collect logs
              </p>
            </div>
          </div>

          {/* Ticket Number */}
          <div>
            <label
              htmlFor="ticket-number"
              className="block text-sm font-medium text-gray-300 mb-1.5"
            >
              Ticket / Reference Number{' '}
              <span className="text-gray-600">(optional)</span>
            </label>
            <input
              id="ticket-number"
              type="text"
              value={ticketNumber}
              onChange={(e) => setTicketNumber(e.target.value)}
              placeholder="INC-12345"
              className="input-field"
              autoComplete="off"
            />
          </div>

          {/* Notes */}
          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-medium text-gray-300 mb-1.5"
            >
              Notes <span className="text-gray-600">(optional)</span>
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe the suspected compromise or any relevant context..."
              rows={3}
              className="input-field resize-y"
            />
          </div>
        </div>

        {/* Submission Error */}
        {submitError && (
          <div className="p-4 bg-red-900/30 border border-red-700/50 rounded-lg flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-300">
                Failed to create investigation
              </p>
              <p className="text-sm text-red-400 mt-1">{submitError}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/cases')}
            className="btn-secondary"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || submitting}
            className="btn-primary flex items-center gap-2"
          >
            {submitting && (
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            {submitting ? 'Creating...' : 'Create Case'}
          </button>
        </div>
      </form>
    </div>
  );
}
